import { endOfDay } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateSwiftlaneUser } from '@/api/keyless/swiftlane/create-swiftlane-user'
import { useGetSwiftlaneMatchingUsers } from '@/api/keyless/swiftlane/get-swiftlane-matching-users'
import { useGetSwiftlaneResident } from '@/api/keyless/swiftlane/get-swiftlane-resident'
import { useGetSwiftlaneUserGroups } from '@/api/keyless/swiftlane/get-swiftlane-user-groups'
import { useMapSwiftlaneUser } from '@/api/keyless/swiftlane/map-swiftlane-user'
import { useUpdateSwiftlaneResident } from '@/api/keyless/swiftlane/update-swiftlane-resident'
import { useIsSwiftlaneBuildingConnected } from '@/api/property-keyless/swiftlane/use-is-swiftlane-building-connected'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { Collapse } from '@/components/ui/collapse'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Flex } from '@/components/ui/flex'
import { Select } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory, toastFactory } from '@/factories'
import { DirtyFieldChange } from '@/hooks'
import { formatDateOnly, parseLocalDate } from '@/utils/date'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { computeKeylessArrayDiff, computeKeylessDateDiff } from '../keyless-access.utils'
import { KeylessBuildingNotConnected } from '../keyless-access-building-not-connected'
import { KeylessNotConnected } from '../keyless-access-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { SwiftlaneMatchUser, SwiftlaneTabProps } from './swiftlane-tab.types'

// The BE returns the resident expiration as epoch millis (mirroring the value we
// PUT via `.getTime()`), occasionally as a numeric string. Fall back to ISO-date
// parsing for safety, and return null for anything unparseable so downstream
// date formatting never receives an Invalid Date.
const parseSwiftlaneExpiration = (value: string | number): Date | null => {
  const epoch = typeof value === 'number' ? value : Number(value)
  const parsed =
    Number.isFinite(epoch) && String(value).trim() !== ''
      ? new Date(epoch)
      : parseLocalDate(String(value))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const SwiftlaneTab = ({
  clientUserId,
  buildingId,
  clientEmail,
  isEditing,
  onEditingChange,
  onRegisterSave,
  onRegisterGetChanges,
  onLoadingChange,
  onConnectionChange,
}: SwiftlaneTabProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const [selectedAccessLevelIds, setSelectedAccessLevelIds] = useState<string[]>([])
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [isConnectedState, setIsConnectedState] = useState(false)
  const [matchingUsers, setMatchingUsers] = useState<SwiftlaneMatchUser[]>([])
  const [selectedConnectUserId, setSelectedConnectUserId] = useState<string | null>(null)
  const [showMatchList, setShowMatchList] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)
  const [showIntercomWarning, setShowIntercomWarning] = useState(false)

  const {
    data: resident,
    isLoading: isResidentLoading,
    isSuccess: isResidentSuccess,
    refetch: refetchResident,
  } = useGetSwiftlaneResident({ buildingId, userId: clientUserId })
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsSwiftlaneBuildingConnected(buildingId)

  // Note: gated on `isSuccess`, not just the raw response - React Query keeps the last
  // successful value in `data` even after a later background refetch fails, which would
  // otherwise leave this resident looking connected once a stale value was ever fetched.
  const isConnected = (isResidentSuccess && Boolean(resident)) || isConnectedState

  const { data: userGroups = [] } = useGetSwiftlaneUserGroups({ buildingId }, isConnected)
  const { mutateAsync: getMatchingUsers, isPending: isCheckingMatch } =
    useGetSwiftlaneMatchingUsers()
  const { mutateAsync: createUser, isPending: isCreating } = useCreateSwiftlaneUser()
  const { mutateAsync: mapUser } = useMapSwiftlaneUser()
  const { mutateAsync: updateResident } = useUpdateSwiftlaneResident()

  useEffect(() => {
    if (!resident) return
    setSelectedAccessLevelIds(resident.groups.map((g) => g.id))
    setExpirationDate(
      resident.expirationDate ? parseSwiftlaneExpiration(resident.expirationDate) : null,
    )
  }, [resident])

  const handleSaveAccessRef = useRef<() => Promise<void>>(undefined)

  const handleOpenSaveConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.swiftlane.confirmAccess.confirmBody'),
      confirmText: t('clients:keylessAccess.swiftlane.confirmAccess.confirmText'),
      onConfirm: () => handleSaveAccessRef.current?.(),
      title: t('clients:keylessAccess.swiftlane.confirmAccess.confirmTitle'),
      variant: 'warning',
    })

  useEffect(() => {
    if (!isEditing) return
    onRegisterSave?.(handleOpenSaveConfirm)
    return () => onRegisterSave?.(null)
  }, [isEditing, onRegisterSave])

  const currentValuesRef = useRef({ expirationDate, selectedAccessLevelIds, userGroups })
  currentValuesRef.current = { expirationDate, selectedAccessLevelIds, userGroups }
  const initialRef = useRef<{ accessLevelIds: string[]; expDate: Date | null }>({
    accessLevelIds: [],
    expDate: null,
  })

  useEffect(() => {
    if (!isEditing) {
      onRegisterGetChanges?.(null)
      return
    }
    initialRef.current = {
      accessLevelIds: currentValuesRef.current.selectedAccessLevelIds,
      expDate: currentValuesRef.current.expirationDate,
    }
    onRegisterGetChanges?.(() => {
      const {
        expirationDate: curDate,
        selectedAccessLevelIds: curIds,
        userGroups: groups,
      } = currentValuesRef.current
      const { accessLevelIds: initIds, expDate: initDate } = initialRef.current
      const changes: DirtyFieldChange[] = []
      const accessDiff = computeKeylessArrayDiff(
        initIds,
        curIds,
        'Access Level',
        (id) => groups.find((g) => g.id === id)?.name ?? id,
      )
      if (accessDiff) changes.push(accessDiff)
      const dateDiff = computeKeylessDateDiff(initDate, curDate, 'Expiration Date')
      if (dateDiff) changes.push(dateDiff)
      return changes
    })
    return () => onRegisterGetChanges?.(null)
  }, [isEditing, onRegisterGetChanges])

  const isTabLoading =
    isResidentLoading || isCheckingMatch || isRefetching || isLoadingBuildingConnection

  useEffect(() => {
    onLoadingChange?.(isTabLoading)
    return () => onLoadingChange?.(false)
  }, [isTabLoading, onLoadingChange])

  useEffect(() => {
    if (isResidentLoading || isLoadingBuildingConnection) return
    onConnectionChange?.(isBuildingConnected && isConnected)
  }, [
    isConnected,
    isBuildingConnected,
    isResidentLoading,
    isLoadingBuildingConnection,
    onConnectionChange,
  ])

  const handleCreateNewUser = async () => {
    setShowIntercomWarning(false)
    try {
      await createUser({ buildingId, userEmail: clientEmail })
      setIsConnectedState(true)
      toastFactory.success(t('clients:keylessAccess.swiftlane.toasts.createSuccess'))
      void refetchResident()
    } catch (error) {
      // Note: BE creates the Swiftlane user first and the Intercom Entry second. A duplicate
      // "FIRSTNAME L." makes the Entry fail, so BE answers 500 although the user is created and
      // linked to the resident. Re-read the resident (the same source the tab uses to decide
      // "connected") so a PM isn't told it failed and retries; a real failure leaves no resident.
      try {
        const { data: createdResident } = await refetchResident()
        if (createdResident) {
          setIsConnectedState(true)
          setShowIntercomWarning(true)
          toastFactory.success(t('clients:keylessAccess.swiftlane.toasts.createSuccess'))
          return
        }
      } catch {
        // Falls through to the original error below.
      }
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleFetchConnectList = async () => {
    try {
      const users = await getMatchingUsers({ buildingId, userEmail: clientEmail })
      if (users.length === 0) {
        toastFactory.error(t('clients:keylessAccess.swiftlane.toasts.noAccountsFound'))
      } else {
        setMatchingUsers(users)
        setShowMatchList(true)
      }
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleConnectConfirmed = async () => {
    if (!selectedConnectUserId) return
    try {
      await mapUser({ swiftlaneUserId: selectedConnectUserId, userId: clientUserId })
      setIsConnectedState(true)
      setShowMatchList(false)
      toastFactory.success(t('clients:keylessAccess.swiftlane.toasts.connectSuccess'))
      void refetchResident()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSaveAccess = async () => {
    try {
      await updateResident({
        buildingId,
        expirationDate: expirationDate ? endOfDay(expirationDate).getTime() : undefined,
        userEmail: clientEmail,
        userGroupIds: selectedAccessLevelIds,
      })
      toastFactory.success(t('clients:keylessAccess.swiftlane.toasts.accessGrantedSuccess'))
      setIsRefetching(true)
      void refetchResident().finally(() => setIsRefetching(false))
      onEditingChange(false)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }
  handleSaveAccessRef.current = handleSaveAccess

  const handleOpenCreateConfirm = () => {
    setShowMatchList(false)
    setSelectedConnectUserId(null)
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.swiftlane.createUser.confirmBody'),
      confirmText: t('clients:keylessAccess.swiftlane.createUser.confirmText'),
      onConfirm: handleCreateNewUser,
      title: t('clients:keylessAccess.swiftlane.createUser.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleOpenConnectConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.swiftlane.connectUser.confirmBody'),
      confirmText: t('clients:keylessAccess.swiftlane.connectUser.confirmText'),
      onConfirm: handleConnectConfirmed,
      title: t('clients:keylessAccess.swiftlane.connectUser.confirmTitle'),
      variant: 'warning',
    })
  }

  if (isTabLoading) return <KeylessTabSkeleton rowCount={2} />

  if (!isBuildingConnected) {
    return (
      <KeylessBuildingNotConnected
        title={t('clients:keylessAccess.swiftlane.buildingNotConnected.title')}
        message={t('clients:keylessAccess.swiftlane.buildingNotConnected.message')}
      />
    )
  }

  if (!isConnected) {
    const connectOptions = matchingUsers.map((u) => ({
      label: u.name || u.email || u.id,
      value: u.id,
    }))
    return (
      <S.TabContent>
        <KeylessNotConnected
          label={t('clients:keylessAccess.swiftlane.label')}
          isCreating={isCreating}
          isConnecting={isCheckingMatch}
          isConnectDisabled={showMatchList}
          onCreateNew={handleOpenCreateConfirm}
          onConnectExisting={() => void handleFetchConnectList()}
        />
        <Collapse in={showMatchList} animation="grow">
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.swiftlane.selectUser.label')}
              </Typography>
            </Flex>
            <Flex gap={12} justify="flex-end" align="center">
              <Select
                size="small"
                options={connectOptions}
                value={selectedConnectUserId}
                onChange={(value: string) => setSelectedConnectUserId(value)}
                onReset={() => setSelectedConnectUserId(null)}
              />
              <Button
                size="small"
                disabled={!selectedConnectUserId}
                onClick={handleOpenConnectConfirm}
              >
                {t('common:actions.confirm')}
              </Button>
            </Flex>
          </S.FieldRow>
        </Collapse>
      </S.TabContent>
    )
  }

  const accessLevelOptions = userGroups.map((g) => ({ label: g.name, value: g.id }))
  const selectedGroups = userGroups.filter((g) => selectedAccessLevelIds.includes(g.id))

  const intercomWarning = showIntercomWarning ? (
    <Alert
      type="info"
      message={t('clients:keylessAccess.swiftlane.toasts.createIntercomWarning')}
      onClose={() => setShowIntercomWarning(false)}
    />
  ) : null

  if (!isEditing) {
    return (
      <S.TabContent>
        {intercomWarning}
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.swiftlane.accessLevel.label')}
            </Typography>
          </Flex>
          <S.FieldValue>
            {selectedGroups.length > 0 ? (
              <Flex gap={6} wrap="wrap">
                {selectedGroups.map((g) => (
                  <Badge key={g.id} color="blue" size="small" variant="light">
                    {g.name}
                  </Badge>
                ))}
              </Flex>
            ) : (
              EMPTY_VALUE
            )}
          </S.FieldValue>
        </S.FieldRow>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.swiftlane.expirationDate.label')}
            </Typography>
          </Flex>
          <S.FieldValue>
            {expirationDate ? formatDateOnly(expirationDate) : EMPTY_VALUE}
          </S.FieldValue>
        </S.FieldRow>
      </S.TabContent>
    )
  }

  return (
    <S.TabContent>
      <S.FieldRow>
        <Flex gap={6} align="center">
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {t('clients:keylessAccess.swiftlane.accessLevel.label')}
          </Typography>
        </Flex>
        <ChipSelect
          size="small"
          options={accessLevelOptions}
          value={selectedAccessLevelIds}
          placeholder={t('clients:keylessAccess.swiftlane.accessLevel.placeholder')}
          onChange={setSelectedAccessLevelIds}
        />
      </S.FieldRow>
      <S.FieldRow>
        <Flex gap={6} align="center">
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {t('clients:keylessAccess.swiftlane.expirationDate.label')}
          </Typography>
        </Flex>
        <DatePickerInput
          size="small"
          placeholder={t('clients:keylessAccess.swiftlane.expirationDate.placeholder')}
          value={expirationDate}
          minDate={new Date()}
          onChange={setExpirationDate}
          onRestore={() => setExpirationDate(null)}
        />
      </S.FieldRow>
    </S.TabContent>
  )
}
